import type { PaginationState } from "@tanstack/react-table"
import { AxiosError } from "axios"
import type { ParseKeys } from "i18next"
import type { MutableRefObject } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  findProductionSitesForSupplierQueryKey,
  useCreateProductionSite,
  useDeleteProductionSite,
  useLinkBusinessRelationshipToProductionSite,
} from "~/gen/api"
import { useUpdateProductionSite } from "~/gen/api/hooks/useUpdateProductionSite.ts"
import type { FindProductionSitesForSupplierResponseDto } from "~/gen/api/types/FindProductionSitesForSupplierResponseDto"
import type { ProductionSiteFormValues } from "~/pages/SupplierPortal/ProductionSites/schemas/productionSiteForm.schema"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

import {
  type CreateProductionSiteResultKind,
  type CreatedProductionSiteSnapshot,
  getCreatePayloadFromFormValues,
  getUpdatePayloadFromFormValues,
  parseCreateProductionSiteMutationPayload,
  toCreatedProductionSiteSnapshot,
} from "./edit-production-site-drawer.helpers"
import { hasNonParentFieldChanges } from "../utils/productionSiteEdit.utils"
import {
  type BusinessRelationshipOption,
  hasParentCompanySelectionChanged,
  resolveParentCompanyOrganizationId,
  toParentCompanyLink,
} from "../utils/productionSiteParentCompany.utils"

interface UseProductionSiteDrawerMutationsParams {
  readonly isEdit: boolean
  readonly onClose: () => void
  readonly pagination: PaginationState
  readonly productionSite?: FindProductionSitesForSupplierResponseDto
  readonly onProductionSiteCreated?: (
    productionSiteId: string,
    kind: CreateProductionSiteResultKind | undefined,
    createdSite?: CreatedProductionSiteSnapshot
  ) => void
  readonly businessRelationshipOptions: ReadonlyArray<BusinessRelationshipOption>
  readonly lastCreateFormValuesRef: MutableRefObject<ProductionSiteFormValues | null>
}

interface UseProductionSiteDrawerMutationsResult {
  readonly isSubmitting: boolean
  readonly handleSubmit: (values: ProductionSiteFormValues) => void
  readonly deleteProductionSiteById: (productionSiteId: string) => void
}

const invalidateProductionSitesQuery = (pagination: PaginationState): void => {
  queryClient.invalidateQueries({
    queryKey: findProductionSitesForSupplierQueryKey({
      pagination: {
        take: pagination.pageSize,
        skip: pagination.pageIndex,
      },
    }),
  })
}

const getCreateSuccessTranslation = (
  t: (key: ParseKeys<"supplier">) => string,
  kind: CreateProductionSiteResultKind | undefined,
): { readonly title: string; readonly message: string } => {
  if (kind === "merged") {
    return {
      title: t("productionSites.create.successMerged.title"),
      message: t("productionSites.create.successMerged.message"),
    }
  }

  return {
    title: t("productionSites.create.successCreated.title"),
    message: t("productionSites.create.successCreated.message"),
  }
}

export const useProductionSiteDrawerMutations = ({
  isEdit,
  onClose,
  pagination,
  productionSite,
  onProductionSiteCreated,
  businessRelationshipOptions,
  lastCreateFormValuesRef,
}: UseProductionSiteDrawerMutationsParams): UseProductionSiteDrawerMutationsResult => {
  const { t } = useTranslation("supplier")

  const createMutation = useCreateProductionSite({
    mutation: {
      onSuccess: (data) => {
        const responsePayload = parseCreateProductionSiteMutationPayload(data.data)
        const productionSiteId =
          responsePayload?.productionSite?.id ?? responsePayload?.id
        const createSuccessTranslation = getCreateSuccessTranslation(
          t,
          responsePayload?.kind,
        )

        onClose()
        showSuccessNotification({
          message: createSuccessTranslation.message,
          title: createSuccessTranslation.title,
        })
        invalidateProductionSitesQuery(pagination)

        if (productionSiteId !== undefined) {
          const createdSite =
            lastCreateFormValuesRef.current === null
              ? undefined
              : toCreatedProductionSiteSnapshot(
                  productionSiteId,
                  lastCreateFormValuesRef.current
                )

          onProductionSiteCreated?.(
            productionSiteId,
            responsePayload?.kind,
            createdSite
          )
        }

        lastCreateFormValuesRef.current = null
      },
      onError: (error: AxiosError) => {
        showAxiosError({
          notificationTitle: t("productionSites.create.error"),
          notificationMessage: error.message,
          showNotification: true,
          error,
        })
      },
    },
  })

  const { mutate: deleteProductionSite } = useDeleteProductionSite({
    mutation: {
      onSuccess: () => {
        showSuccessNotification({
          message: t("productionSites.delete.success.message"),
          title: t("productionSites.delete.success.title"),
        })
        invalidateProductionSitesQuery(pagination)
        onClose()
      },
      onError: (error: AxiosError) => {
        showAxiosError({
          notificationTitle: t("productionSites.delete.error"),
          notificationMessage: error.message,
          showNotification: true,
          error,
        })
        invalidateProductionSitesQuery(pagination)
      },
    },
  })

  const updateMutation = useUpdateProductionSite()
  const linkParentCompanyMutation = useLinkBusinessRelationshipToProductionSite()

  const submitEdit = async (
    values: ProductionSiteFormValues,
    existingProductionSite: FindProductionSitesForSupplierResponseDto
  ): Promise<void> => {
    const hasOtherFieldChanges = hasNonParentFieldChanges(values, existingProductionSite)
    const originalParentCompanyId = resolveParentCompanyOrganizationId(
      toParentCompanyLink(existingProductionSite.parentCompany),
      businessRelationshipOptions
    )
    const hasParentCompanyChanged = hasParentCompanySelectionChanged(
      values.parentCompany,
      originalParentCompanyId,
      businessRelationshipOptions
    )

    if (!hasOtherFieldChanges && !hasParentCompanyChanged) {
      onClose()
      return
    }

    let hasSavedNonParentFields = false
    try {
      if (hasOtherFieldChanges) {
        await updateMutation.mutateAsync({
          productionSiteId: existingProductionSite.id,
          ...getUpdatePayloadFromFormValues(values),
        })
        hasSavedNonParentFields = true
      }

      if (hasParentCompanyChanged) {
        await linkParentCompanyMutation.mutateAsync({
          data: {
            productionSiteId: existingProductionSite.id,
            ownerOrganizationId: values.parentCompany,
          },
        })
      }

      onClose()
      invalidateProductionSitesQuery(pagination)
      showSuccessNotification({
        message: t("productionSites.update.success.message"),
        title: t("productionSites.update.success.title"),
      })
    } catch (error) {
      invalidateProductionSitesQuery(pagination)
      const axiosError =
        error instanceof AxiosError
          ? error
          : new AxiosError("Failed to update production site")
      const notificationMessage =
        hasSavedNonParentFields && hasParentCompanyChanged
          ? t("productionSites.update.partialSave.message")
          : axiosError.message
      showAxiosError({
        notificationTitle: t("productionSites.update.error"),
        notificationMessage,
        showNotification: true,
        error: axiosError,
      })
    }
  }

  const handleSubmit = (values: ProductionSiteFormValues): void => {
    if (isEdit && productionSite !== undefined) {
      void submitEdit(values, productionSite)
      return
    }

    lastCreateFormValuesRef.current = values
    createMutation.mutate(getCreatePayloadFromFormValues(values))
  }

  return {
    isSubmitting: isEdit
      ? updateMutation.isPending || linkParentCompanyMutation.isPending
      : createMutation.isPending,
    handleSubmit,
    deleteProductionSiteById: (productionSiteId: string): void => {
      deleteProductionSite({ productionSiteId })
    },
  }
}
